import type { CityConfig } from "@/pages/city-template/types";

export const canbyConfig: CityConfig = {
  city: "Canby",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "canby",
  route: "/cities/canby",
  title: "Emergency Passport Canby, OR | Fast Passport Renewal & Expedited Services",
  metaDescription: "Emergency passport and expedited passport services in Canby, OR — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Canby and Clackamas County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Canby, Oregon",
  heroSubtitle: "Heading to Mexico for the holidays? Cruise from Seattle? Passport expired before your big trip? Fast Passport Center helps Canby and Clackamas County travelers get passports fast — as fast as 24 hours. A+ BBB rated. No office visit required for most services.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Canby%20Oregon%20small%20town%20pastoral%20landscape%20with%20Willamette%20River%20flowing%20through%20fertile%20farmlands%2C%20lush%20agricultural%20fields%20with%20crops%20and%20nursery%20greenhouses%2C%20Mount%20Hood%20visible%20in%20the%20distance%20at%20golden%20hour%2C%20peaceful%20rural%20Pacific%20Northwest%20atmosphere%2C%20warm%20amber%20harvest%20light%2C%20photorealistic%20landscape%20photography%2C%20no%20text%20or%20signs%20visible&width=1800&height=700&seq=canby-or-hero-01&orientation=landscape",
  heroImageAlt: "Canby Oregon pastoral landscape with Willamette River, farmlands, and Mount Hood in the distance",
  geoPosition: "45.2637;-122.6923",
  geoPlacename: "Canby, Oregon",
  lastModified: "2026-06-04",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "300 5th Ave, Suite 600, Seattle, WA 98104",
    distance: "About 175 miles north — 3 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Canby", zips: "97013" },
    { name: "Wilsonville", zips: "97070" },
    { name: "Aurora", zips: "97002" },
    { name: "Molalla", zips: "97038" },
    { name: "Hubbard", zips: "97032" },
    { name: "Donald", zips: "97020" },
    { name: "Newberg", zips: "97132" },
    { name: "Oregon City", zips: "97045" },
    { name: "Gladstone", zips: "97027" },
    { name: "West Linn", zips: "97068" },
    { name: "Lake Oswego", zips: "97034–97035" },
    { name: "Tualatin", zips: "97062" },
    { name: "Sherwood", zips: "97140" },
    { name: "Portland", zips: "97201–97299" },
    { name: "Woodburn", zips: "97071" },
    { name: "Mount Angel", zips: "97362" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from your Canby home in minutes." },
    { icon: "ri-truck-line", title: "Ship Overnight from Canby", desc: "FedEx and UPS locations in Canby and nearby Oregon City and Tualatin offer overnight shipping to our processing center." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application reviewed before submission — important for rural Clackamas County travelers who can't afford to have documents rejected." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the nearest regional passport agency — no public appointment hunting needed." },
  ],
  testimonials: [
    {
      name: "Linda P.",
      location: "Canby, OR",
      rating: 5,
      text: "Family cruise from Seattle and my husband's passport expired. We live in Canby and the nearest passport office was a hassle. Fast Passport Center made it simple — shipped from Oregon City FedEx and had the renewed passport in 7 days. Saved our vacation.",
      service: "Passport Renewal",
    },
    {
      name: "Mark H.",
      location: "Canby, OR",
      rating: 5,
      text: "Agricultural conference in Amsterdam announced with 9 days notice. Fast Passport Center handled emergency processing and I had my passport in 3 days. Canby farmers need this service — you never know when an international trip happens.",
      service: "Emergency Passport",
    },
    {
      name: "Susan K.",
      location: "Molalla, OR",
      rating: 5,
      text: "My daughter needed her first passport for a Mexico school trip. Fast Passport Center walked us through the child passport process — especially the two-parent consent part. Had it in 9 days. Molalla families, highly recommend.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "canby-post",
      name: "Canby Post Office",
      type: "USPS Passport Acceptance",
      address: "175 N Grant St",
      city: "Canby",
      state: "OR",
      zip: "97013",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Downtown Canby",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance location in Canby. Appointment required — book online in advance. Photo services may be available on-site.",
      icon: "ri-mail-line",
    },
    {
      id: "oregon-city-post",
      name: "Oregon City Post Office",
      type: "USPS Passport Acceptance",
      address: "610 7th St",
      city: "Oregon City",
      state: "OR",
      zip: "97045",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Oregon City — about 8 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Oregon City option — often has better appointment availability for Canby and south Clackamas County residents.",
      icon: "ri-mail-line",
    },
    {
      id: "clackamas-county",
      name: "Clackamas County Clerk",
      type: "County Passport Acceptance",
      address: "1710 Red Soils Ct, Suite 100",
      city: "Oregon City",
      state: "OR",
      zip: "97045",
      phone: "503-655-8551",
      hours: "Mon–Fri 8:30am–4:30pm (by appointment)",
      distance: "Oregon City — County Government Center, about 8 miles north",
      website: "https://www.clackamas.us/recording",
      websiteLabel: "Clackamas County Clerk",
      notes: "County clerk passport acceptance. Appointment required — often faster scheduling than USPS. Call ahead to confirm passport services.",
      icon: "ri-government-line",
    },
    {
      id: "wilsonville-post",
      name: "Wilsonville Post Office",
      type: "USPS Passport Acceptance",
      address: "29020 SW Town Center Loop E",
      city: "Wilsonville",
      state: "OR",
      zip: "97070",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Wilsonville — about 8 miles northwest",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Wilsonville option for northwest Canby and Aurora area residents.",
      icon: "ri-mail-line",
    },
    {
      id: "newberg-post",
      name: "Newberg Post Office",
      type: "USPS Passport Acceptance",
      address: "401 E 1st St",
      city: "Newberg",
      state: "OR",
      zip: "97132",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Newberg — about 10 miles west",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Newberg option for west Canby and Yamhill County border residents.",
      icon: "ri-mail-line",
    },
    {
      id: "gladstone-post",
      name: "Gladstone Post Office",
      type: "USPS Passport Acceptance",
      address: "18905 Portland Ave",
      city: "Gladstone",
      state: "OR",
      zip: "97027",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Gladstone — about 7 miles northeast",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Gladstone option for northeast Canby and Clackamas River area residents.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Canby before international travel?",
      a: "Yes. Through Fast Passport Center, Canby travelers can get an expedited passport in as little as 2–9 business days, and emergency processing is available in as fast as 24 hours depending on eligibility and travel documentation. Canby's rural Clackamas County location doesn't slow us down — start your order online today.",
    },
    {
      q: "How fast can I renew my passport in Canby?",
      a: "Fast passport renewal in Canby is available through Fast Passport Center in as little as 3 business days with our expedited service, or 3–5 weeks with our standard expedited option. Renewal applications are eligible if your current passport was issued at age 16 or older and within the last 15 years. No office visit required for most renewals.",
    },
    {
      q: "Is it difficult to get a passport expedited from a rural area like Canby?",
      a: "Not at all. Fast Passport Center works entirely remotely — you start online, ship your documents overnight from any FedEx or UPS near Canby (Oregon City has multiple locations), and we handle the rest. Rural Clackamas County residents use our service regularly. Distance from a passport agency doesn't matter.",
    },
    {
      q: "Can I get a same-day passport appointment near Canby?",
      a: "Same-day passport appointments at the regional passport agency in Seattle are only available for documented life-or-death emergencies and are not guaranteed. For most urgent passport situations near Canby, Fast Passport Center is the faster and more predictable option — no three-hour Seattle drive required.",
    },
    {
      q: "Can I expedite a child passport in Canby?",
      a: "Yes. Child passport expediting in Canby is possible through Fast Passport Center, but child passports (for minors 15 and under) require an in-person acceptance step with parental consent. We provide a clear checklist and instructions. Once the acceptance step is complete at the Canby Post Office or Oregon City, we handle the expedited processing.",
    },
    {
      q: "What should I do if I lost my passport before a trip?",
      a: "If your passport is lost or stolen before an upcoming trip, act immediately. You will need to complete Form DS-64 to report it and apply for a replacement. Fast Passport Center guides you through this process and can expedite your replacement passport. Don't delay — lost passport replacements take extra steps.",
    },
    {
      q: "Does Canby have a passport agency?",
      a: "Canby, Oregon does not have a regional passport agency. The closest regional agencies are the Seattle Passport Agency in Washington (about 175 miles north) and the San Francisco Passport Agency in California (about 650 miles south). Both require appointments and proof of travel within 14 days. Fast Passport Center is the faster, more convenient solution for Canby residents.",
    },
    {
      q: "How long does a standard passport take in Canby?",
      a: "Standard USPS passport processing from Canby runs 6–13 weeks — sometimes longer during peak travel season. If your trip is sooner, you need expedited processing. Fast Passport Center offers Canby travelers processing as fast as 24 hours.",
    },
    {
      q: "Can I renew my passport in Canby without visiting a passport office?",
      a: "Yes. Most Canby adults renewing a passport can do so entirely by mail — no in-person visit required. Start your order online with Fast Passport Center, complete your documents at home, and ship them overnight from FedEx or UPS in Oregon City or Wilsonville.",
    },
    {
      q: "What airports do Canby travelers fly from?",
      a: "Canby travelers primarily use Portland International Airport (PDX) — about 25 miles north — for all domestic and international departures. PDX offers direct flights to Amsterdam, London, Reykjavik, Tokyo, Vancouver, and more. Fast Passport Center can help you meet your deadline for any PDX departure.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Canby", address: "1400 N Holly St, Canby, OR 97013", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS — Canby", address: "175 N Grant St, Canby, OR 97013", hours: "Daily 7 AM – 10 PM" },
    { name: "FedEx Office — Oregon City", address: "1651 Beavercreek Rd, Oregon City, OR 97045", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 8 AM – 8 PM" },
    { name: "The UPS Store — Oregon City", address: "18120 SE McLoughlin Blvd, Oregon City, OR 97045", hours: "Mon–Fri 8 AM – 7 PM" },
    { name: "Walmart — Oregon City", address: "700 Hawthorne Ave, Oregon City, OR 97045", hours: "Daily 7 AM – 11 PM" },
    { name: "Walgreens — Oregon City", address: "19000 S Beavercreek Rd, Oregon City, OR 97045", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS — Oregon City", address: "19000 Molalla Ave, Oregon City, OR 97045", hours: "Daily 7 AM – 10 PM" },
    { name: "FedEx Office — Wilsonville", address: "29040 SW Town Center Loop E, Wilsonville, OR 97070", hours: "Mon–Fri 7 AM – 10 PM" },
    { name: "Walgreens — Gladstone", address: "18525 Portland Ave, Gladstone, OR 97027", hours: "Daily 8 AM – 10 PM" },
    { name: "Walmart — Canby", address: "1751 N Holly St, Canby, OR 97013", hours: "Daily 7 AM – 11 PM" },
  ],
  dmvLocations: [
    {
      name: "Oregon City DMV Office",
      address: "1021 S Molalla Ave, Oregon City, OR 97045",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Closest DMV to Canby. Clackamas County. Driver licenses, REAL ID, ID cards. Appointment recommended.",
      featured: true,
    },
    {
      name: "Clackamas DMV Office",
      address: "5060 SE Hwy 212, Clackamas, OR 97015",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Clackamas County option for northeast Canby residents.",
      featured: false,
    },
    {
      name: "Wilsonville DMV Office",
      address: "29020 SW Town Center Loop E, Wilsonville, OR 97070",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Wilsonville option for northwest Canby and Aurora area residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "Primary international airport — about 25 miles north",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "PDX offers direct international flights to Amsterdam, London, Reykjavik, Tokyo, Vancouver, and more. The busiest travel seasons are May–August and December–January — plan passport needs well in advance.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: true,
    },
    {
      code: "SEA",
      name: "Seattle-Tacoma International Airport",
      distance: "Alternative hub — about 175 miles north",
      address: "17801 International Blvd, SeaTac, WA 98158",
      note: "Some Canby travelers use SEA for cruise departures or additional flight options. Passport required for all international departures.",
      flightUrl: "https://www.portseattle.org/sea-tac",
      flightLabel: "SEA Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying from PDX in 24–72 hours? Our emergency courier service handles urgent passport needs for Canby and rural Clackamas County travelers — no Seattle trip required.",
      bullets: ["As fast as 24 hours", "Reserved agency submission slots", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Canby and Clackamas County travelers renew fast — perfect for family vacations, Mexico trips, and cruises from Seattle.",
      bullets: ["Eligible for expedited processing", "Ship from Oregon City FedEx or UPS", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport before a trip? We guide Canby travelers through Form DS-64 and the replacement process — urgent timelines available.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Child passport expedited service for Canby families — minors under 16 require in-person acceptance and parental consent. We provide clear instructions for every step.",
      bullets: ["Both parents must typically consent", "Clear checklist provided", "Expedited options after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Canby residents — we guide you through the acceptance clerk process and handle expedited processing.",
      bullets: ["Step-by-step checklist", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Passport Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — we submit through reserved agency channels, not the public queue. Canby travelers get the same access as Portland residents.",
      bullets: ["State Dept. registered courier", "Reserved daily submission slots", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Services",
      description: "Passport name changes after marriage or divorce, damaged passport replacement, second passports for frequent travelers — all handled with expedited courier access.",
      bullets: ["Name change after marriage/divorce", "Damaged passport replacement", "Second passport for frequent travel"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Canby, Oregon travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Canby residents needing passports faster than standard government mail timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Canby, OR residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement service for Canby travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Canby families with minors under 16." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier passport submission service serving Canby, Oregon." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Canby travelers often assume 6–8 weeks is safe — it's not. Government processing has stretched to 10–14+ weeks during peak travel season. Apply as soon as you book, especially for summer travel and holiday cruises." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from local drugstores often get rejected for lighting, background, or expression issues. We provide exact specifications and can review photos before your application is submitted." },
    { icon: "ri-file-damage-line", title: "Incomplete or Incorrect Forms", description: "One wrong date, one missing signature — your application is returned and you lose weeks. Our pre-submission checklist catches errors before they become expensive delays for Canby travelers." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Type", description: "Not all birth certificates are accepted. Some require a long-form certified copy with a raised seal. Fast Passport Center tells you exactly what you need based on your specific situation." },
    { icon: "ri-calendar-close-line", title: "Assuming the Post Office is Fast Enough", description: "Standard USPS passport processing from Canby runs 6–13 weeks. If your trip is in under 10 weeks, standard processing is a gamble. Use Fast Passport Center instead." },
    { icon: "ri-map-pin-time-line", title: "Name Mismatch Between Passport and Airline Ticket", description: "Your passport name must match your airline ticket exactly — especially for international flights out of PDX. Middle names, hyphens, and suffixes cause more rejections than expected." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Canby travel date. Complete your secure order online — takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We confirm your details and send you a personalized document checklist — exactly what you need, nothing more." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Canby", description: "Drop off at any FedEx or UPS near Canby or Oregon City — most locations are open evenings. No office visit to our facility required." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit your application through our reserved passport agency slots — bypassing the public appointment system entirely." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport is Completed", description: "Your passport is completed within the timeframe you selected and shipped back to you. No guessing, no hoping." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited slots, often weeks out in Seattle", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Seattle (3 hours each way)", fpc: "Ship documents overnight from Canby" },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission slots" },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected" },
    { feature: "Government Shutdowns", agency: "Operations may be impacted", fpc: "Not affected by government shutdowns" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Portland", route: "/cities/portland" },
    { name: "Fast passport renewal in Oregon City", route: "/cities/oregon-city" },
    { name: "Emergency passport help near Wilsonville", route: "/cities/wilsonville" },
    { name: "Rush passport service in West Linn", route: "/cities/west-linn" },
    { name: "Fast passport renewal for Clackamas County travelers", route: "/states/oregon" },
  ],
  fpcOfficeAddress: "5608 17th Avenue NW, Seattle, WA 98107",
  fpcOfficeNote: "After signing up online, we send your personalized checklist and shipping instructions. Ship your documents overnight from any FedEx or UPS in Canby or Oregon City — most are open evenings. No office visit to our facility required for most services.",
  fpcOfficeDistance: "Serving Canby and all of south Clackamas County remotely",
  serviceAreaIntro: "Whether you are in Canby, Aurora, Molalla, Hubbard, or anywhere in south Clackamas County, Fast Passport Center makes expedited passport processing simple — start online, ship overnight, and we handle the rest. Rural location is no barrier.",
  nearbyCitiesIntro: "We serve all Canby and south Clackamas County zip codes. Anywhere in the Willamette Valley agricultural corridor — ship overnight and your documents are at our processing center the next morning.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Canby as simple as possible. Most Canby clients never visit an office.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Canby and rural Clackamas County travelers. We review your documents before submission to catch issues before they become problems.",
  testimonialsIntro: "Real reviews from Canby, Molalla, and south Clackamas County travelers who needed their passports fast and trusted Fast Passport Center.",
  faqIntro: "The most common questions Canby and Clackamas County travelers ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
};